/**
 * Dev-only behaviour switches.
 *
 * `import.meta.env.DEV` is true on the Vite dev server and false in a build —
 * and it is ALSO false under Vitest (which runs PROD-side, `MODE="test"`), so
 * the test suite exercises the shipped defaults, not the dev ones. That is why
 * the decision lives in a pure function tests can call directly.
 *
 * The cast: no `vite/client` types are installed, and adding them would make
 * the tests' deliberate `@ts-expect-error` on `?raw` imports unused.
 */
const IS_DEV = (import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV === true;

/**
 * The tomato timer's length. The dev server shortens it to 1 minute, so the
 * alarm is reachable without waiting 25; the shipped build returns `undefined`,
 * keeping `TomatoTimer`'s own 25-minute default.
 */
export function tomatoDurationMs(dev: boolean): number | undefined {
  return dev ? 60_000 : undefined;
}

/** The timer length for this build. */
export const TOMATO_DURATION_MS = tomatoDurationMs(IS_DEV);

/**
 * The favicon's href. The dev server gets the amber dev mark, so the two
 * servers are never confused at a glance; a build gets the shipped teal mark.
 * Same pure-function trick as `tomatoDurationMs` — `vite.config.ts`'s
 * `transformIndexHtml` feeds it `ctx.server`, and this test can call it
 * directly.
 *
 * The dev mark lives under `src/`, NOT `public/`, so it is never copied into
 * `dist/` — only the shipped `/favicon.svg` reaches production.
 */
export function faviconHref(dev: boolean): string {
  return dev ? '/src/assets/favicon-dev.svg' : '/favicon.svg';
}
